import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Portfolio } from '../api/portfolioApi';

export interface PortfolioState {
  portfolios: Portfolio[];
  selectedPortfolioId: string | null;
  isLoading: boolean;
  error: string | null;
}

const PORTFOLIO_STORAGE_KEY = 'selected_portfolio_v1';

const loadPersistedPortfolioId = (): string | null => {
  try {
    return localStorage.getItem(PORTFOLIO_STORAGE_KEY);
  } catch { return null; }
};

const persistPortfolioId = (id: string | null) => {
  try {
    if (id) {
      localStorage.setItem(PORTFOLIO_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(PORTFOLIO_STORAGE_KEY);
    }
  } catch { /* ignore */ }
};

const initialState: PortfolioState = {
  portfolios: [],
  selectedPortfolioId: loadPersistedPortfolioId(),
  isLoading: false,
  error: null,
};

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setPortfolios: (state, action: PayloadAction<Portfolio[]>) => {
      state.portfolios = action.payload;
    },
    addPortfolio: (state, action: PayloadAction<Portfolio>) => {
      const existingIndex = state.portfolios.findIndex((p) => p.id === action.payload.id);
      if (existingIndex >= 0) {
        state.portfolios[existingIndex] = action.payload;
      } else {
        state.portfolios.push(action.payload);
      }
    },
    updatePortfolio: (state, action: PayloadAction<Portfolio>) => {
      const index = state.portfolios.findIndex((p) => p.id === action.payload.id);
      if (index >= 0) {
        state.portfolios[index] = action.payload;
      }
    },
    removePortfolio: (state, action: PayloadAction<string>) => {
      state.portfolios = state.portfolios.filter((p) => p.id !== action.payload);
    },
    setSelectedPortfolioId: (state, action: PayloadAction<string | null>) => {
      state.selectedPortfolioId = action.payload;
      persistPortfolioId(action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPortfolio: (state) => {
      state.portfolios = [];
      state.selectedPortfolioId = null;
      state.error = null;
      persistPortfolioId(null);
    },
  },
});

export const {
  setPortfolios,
  addPortfolio,
  updatePortfolio,
  removePortfolio,
  setSelectedPortfolioId,
  setLoading,
  setError,
  clearError,
  resetPortfolio,
} = portfolioSlice.actions;

export default portfolioSlice.reducer;

